import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteLoaderData } from 'react-router-dom';
import { api } from '../lib/api';
import { FeatureFlagEvaluationListDto, LoginResponseDto, FeatureFlagListItemDto } from '../lib/ServerApi';
import { Loader } from '@/components/loader';

export type FeatureFlagKey = FeatureFlagListItemDto['key'];

interface FeatureFlagContextType {
    isEnabled: (flagKey: FeatureFlagKey) => boolean;
    flags: FeatureFlagEvaluationListDto | undefined;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
    children: ReactNode;
}

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
    const session = useRouteLoaderData('root') as LoginResponseDto | null;

    const shouldFetch = Boolean(session?.activeOrg && session.activeOrg.id !== 'admin');

    const { data: flags, isLoading } = useQuery<FeatureFlagEvaluationListDto>({
        queryKey: ['feature-flags', session?.activeOrg?.id],
        queryFn: async () => {
            const { data } = await api.featureFlags.listForOrg();
            return data;
        },
        enabled: shouldFetch,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: false,
    });

    const isEnabled = (flagKey: FeatureFlagKey): boolean => {
        if (!flags?.flags) {
            return false;
        }

        const flag = flags.flags.find((f) => f.key === flagKey);
        return flag?.value ?? false;
    };

    const value: FeatureFlagContextType = {
        isEnabled,
        flags,
    };

    if (isLoading) {
        return <Loader />;
    }

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlag() {
    const context = useContext(FeatureFlagContext);
    if (context === undefined) {
        throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
    }
    return context;
}
